import React, { useContext } from "react";
import { NetworkStatus } from "@apollo/client";
import { ColumnDef } from "@tanstack/table-core/build/lib/types";
import { useHistory, useLocation, useParams } from "react-router";
import { SUBMISSION_CATEGORY, SUBMISSION_TYPE_MODULE } from "common/constants";
import { buildLink } from "common/routing";
import { Attachment, isPropertyAttachment } from "common/utils/customObjects";
import {
  ClientSideConfigEntry,
  QueryDescription,
  sourceForSubmissionCategory,
  SUBMISSION_CATEGORY_TO_TABLE,
  SubmissionTableName,
  TABLE_NAMES,
} from "common/utils/queryBuilder";
import { SUBMISSION_CATEGORY_TO_LABEL } from "common/utils/submissions";
import {
  CurrentUserQuery,
  GetQuerySubmissionsQueryVariables,
  useGetQuerySubmissionsLazyQuery,
  useGetSavedViewsQuery,
  useGetSubmissionTableConfigQuery,
} from "../../generated/graphql";
import {
  buildLocalTableInfo,
  useLocalTableDisplayConfig,
} from "../../hooks/useTableDisplayConfig";
import { AuthContext } from "../Authorization/AuthContext";
import { Account } from "../Authorization/types";
import { someActionsEnabled } from "../Common/ActionBar/utils";
import { ACTION_COLUMN_DEF_CONSTANTS } from "../Common/ActionCell";
import { Button } from "../Common/Buttons/Button";
import { CreateSubmissionButton } from "../Common/CreateSubmissionButton";
import {
  createSubmissionRedirect,
  hasPropertyAttachment,
} from "../Common/CreateSubmissionButton/utils";
import { Layout } from "../Common/FullWidthLayout";
import { TasksCell } from "../Common/FullWidthTable/customCells";
import { FullWidthTable } from "../Common/FullWidthTable/FullWidthTable";
import { Attribute } from "../Common/FullWidthTable/types";
import {
  fieldId,
  initializeColumns,
  mergeFieldGroups,
  MinimalColumnDef,
  sanityCheckLocalColumnOrder,
} from "../Common/FullWidthTable/utils";
import LinkCell from "../Common/LinkCell";
import {
  useManualPaginationConfig,
  useQueryDescription,
} from "../Common/Tables/hooks";
import {
  buildColumnDefinitions,
  CellRenderConfig,
} from "../Common/Tables/utils";
import { getFilesTableCustomCells } from "../DocumentUploads/FilesTable";
import {
  CRS_310_REPORT,
  INITIAL_SUBMISSION_TYPE_REPORTS,
  makeSubmissionReports,
} from "../Exports/constants";
import ExportDataButton from "../Exports/ExportDataButton";
import { buildInitialReportTypes } from "../Exports/ExportDataForm";
import { DropdownMenu } from "../Inputs";
import { CUSTOM_PROPERTY_LINK_CELLS } from "../Property/PropertiesTable";
import { QuerySubmissionTableResult } from "./__queries__/table";
import ActionButton from "./ActionButton";
import { canCreateSubmissionTypeFromAuthInfo } from "./utils";

export const SUBMISSIONS_TABLE_REQUIRED_FIELDS = (
  tableName: SubmissionTableName,
) => [
  { table: tableName, name: "id" },
  { table: TABLE_NAMES.PROPERTIES, name: "id" },
  { table: TABLE_NAMES.PROPERTIES, name: "latitude" },
  { table: TABLE_NAMES.PROPERTIES, name: "longitude" },
  { table: TABLE_NAMES.PROPERTIES, name: "streetAddress" },
  { table: TABLE_NAMES.CUSTOM_MAP_GEOMETRIES, name: "id" },
  { table: TABLE_NAMES.CUSTOM_MAP_GEOMETRIES, name: "customMapId" },
  { table: TABLE_NAMES.CUSTOM_MAP_GEOMETRIES, name: "label" },
  { table: tableName, name: "hasSummary" },
  { table: tableName, name: "submissionTypeId" },
  { table: tableName, name: "submissionTypeCategory" },
  { table: tableName, name: "submissionTypeName" },
  { table: tableName, name: "hiddenFromPublic" },
  { table: tableName, name: "workflowStepId" },
];

const submissionCategoryMapOverrides = {
  [SUBMISSION_CATEGORY.INSPECTIONS]: {
    submissionTypeExportReports: [
      INITIAL_SUBMISSION_TYPE_REPORTS.SDE_RESIDENTIAL_REPORT,
      INITIAL_SUBMISSION_TYPE_REPORTS.SDE_NON_RESIDENTIAL_REPORT,
    ],
  },
  [SUBMISSION_CATEGORY.PERMITTING]: {
    exportReportTypes: [CRS_310_REPORT],
    submissionTypeExportReports: undefined,
  },
};

const makeSubmissionCategoryMap = ({
  account,
  columnConfig,
  category,
}: {
  account: Account;
  columnConfig?: Array<ClientSideConfigEntry>;
  category: SUBMISSION_CATEGORY;
}) => {
  const overrides =
    submissionCategoryMapOverrides[
      category as keyof typeof submissionCategoryMapOverrides
    ];

  return {
    tableName: SUBMISSION_CATEGORY_TO_TABLE[category],
    tableContentName: SUBMISSION_CATEGORY_TO_LABEL[category],
    exportReportTypes: makeSubmissionReports({ account, columnConfig }),
    ...overrides,
  };
};

export const SUBMISSION_TABLE_CUSTOM_CELLS: ({
  tableName,
}: {
  tableName: string;
}) => Record<string, CellRenderConfig> = ({ tableName }) => ({
  [`${tableName}.adminLink`]: {
    cell: ({ row }) => {
      const linkProps = row.original[`${tableName}.adminLink`];

      if (!linkProps) {
        return null;
      }

      const url = buildLink(
        "editSubmission",
        { submissionId: linkProps.submissionId },
        {},
      );

      return <LinkCell href={url} target={"_blank"} label={"Link"} />;
    },
  },
  [`${tableName}.submissionTypeName`]: {
    cell: ({ row }) => {
      const id = row.original[`${tableName}.id`];
      const submissionTypeName =
        row.original[`${tableName}.submissionTypeName`];
      const url = buildLink("viewSubmission", { submissionId: id }, {});
      if (!url) {
        return submissionTypeName;
      }
      return (
        <LinkCell href={url} target={"_blank"} label={submissionTypeName} />
      );
    },
  },
  [`${tableName}.tasks`]: {
    cell: ({ row }) => {
      const tasks = row.original[`${tableName}.tasks`] || [];
      return <TasksCell tasks={tasks} />;
    },
  },
});

const isValidSubmissionTypeForCategory = (
  submissionType: NonNullable<
    CurrentUserQuery["currentUser"]
  >["account"]["submissionTypes"][number],
  tableCategory: SUBMISSION_CATEGORY,
  isPermitting: boolean,
) => {
  if (submissionType.category !== tableCategory) return false;
  if (
    !submissionType.attachments.some(att =>
      isPropertyAttachment(att as Attachment),
    )
  )
    return false;
  if (!isPermitting) return true;
  return submissionType.modules.includes(SUBMISSION_TYPE_MODULE.EC_SUBMISSION);
};

const SubmissionsTable = ({ category }: { category: SUBMISSION_CATEGORY }) => {
  const { account, isPublic, user, admin, publicUser } =
    useContext(AuthContext);
  const location = useLocation();
  const history = useHistory();

  const { data: columnConfigResponse, loading: loadingColumnConfig } =
    useGetSubmissionTableConfigQuery({
      variables: { category },
      fetchPolicy: "network-only",
    });

  const submissionCategoryMap = makeSubmissionCategoryMap({
    account: account!,
    columnConfig: columnConfigResponse?.getSubmissionTableConfig.data,
    category,
  });

  const {
    tableContentName,
    tableName,
    exportReportTypes,
    submissionTypeExportReports,
  } = submissionCategoryMap;
  const {
    data: savedViewsResponse,
    loading: loadingSavedViews,
    refetch: refetchSavedViews,
  } = useGetSavedViewsQuery({
    variables: { table: tableName },
    fetchPolicy: "cache-and-network",
  });

  const [
    querySubmissions,
    { previousData, data: currentData, networkStatus, loading, error, refetch },
  ] = useGetQuerySubmissionsLazyQuery({
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  const initialReportTypes = buildInitialReportTypes({
    initialReportTypes: exportReportTypes,
    account,
    submissionTypeNames: submissionTypeExportReports,
  });

  const tableActions = [
    <ExportDataButton
      key={"exportData"}
      initialReportTypes={initialReportTypes}
    />,
  ];

  const actionsColumn: MinimalColumnDef<
    [{ table: TABLE_NAMES; name: string }]
  > = {
    ...ACTION_COLUMN_DEF_CONSTANTS,
    cell: ({ row }) => {
      const propertyId = row.original["Properties.id"];
      const customMapGeometryId = row.original["CustomMapGeometries.id"];

      const property =
        propertyId ?
          {
            __typename: "Property" as const,
            id: propertyId,
            fullAddress:
              row.original["Properties.fullAddress"] ??
              row.original["Properties.streetAddress"] ??
              "",
            longitude: row.original["Properties.longitude"],
            latitude: row.original["Properties.latitude"],
          }
        : undefined;

      const customMapGeometry =
        customMapGeometryId ?
          {
            __typename: "CustomMapGeometry" as const,
            id: customMapGeometryId,
            customMapId: row.original["CustomMapGeometries.customMapId"],
            label: row.original["CustomMapGeometries.label"],
          }
        : undefined;

      const workflowStepId = row.original[`${tableName}.workflowStepId`];
      const submission = {
        hasSummary: row.original[`${tableName}.hasSummary`],
        id: row.original[`${tableName}.id`],
        status: row.original["CertificateUploads.status"],
        property,
        customMapGeometry,
        submissionTypeVersion: {
          submissionType: {
            id: row.original[`${tableName}.submissionTypeId`],
            category: row.original[`${tableName}.submissionTypeCategory`],
            name: row.original[`${tableName}.submissionTypeName`],
            aiActions: [],
            modules: [],
          },
        },
        relatedSubmissions: [],
        category,
        hiddenFromPublic: row.original[`${tableName}.hiddenFromPublic`],
        workflowStep: workflowStepId ? { id: workflowStepId } : undefined,
      };

      return <ActionButton submission={submission} onUpdate={refetch!} />;
    },
  };

  const {
    initialTableState,
    queryDescription,
    updateQueryDescription,
    currentView,
  } = useQueryDescription({
    source: sourceForSubmissionCategory(category),
    defaultSort: [{ id: `${tableName}.createdAt`, desc: true }],
    savedViews: savedViewsResponse?.account?.savedViews,
  });

  const generatedColumnDefinitions = buildColumnDefinitions({
    columnConfig: columnConfigResponse?.getSubmissionTableConfig.data || [],
    cellRenderers: {
      ...getFilesTableCustomCells({ isPublic }),
      ...SUBMISSION_TABLE_CUSTOM_CELLS({ tableName }),
      ...CUSTOM_PROPERTY_LINK_CELLS,
    },
  });

  const { id: tableId } = buildLocalTableInfo({
    entityId: user?.id ?? admin?.id,
    pathname: location.pathname,
  });

  const { getLocalTableState, setLocalColumnOrder } =
    useLocalTableDisplayConfig({
      tableId,
      defaultValue: {
        columnOrder: [
          ...queryDescription.fields.map(field => fieldId(field)),
          ACTION_COLUMN_DEF_CONSTANTS.id,
        ],
        columnSizing: {},
        sorting: [],
      },
    });

  const manualPaginationConfig = useManualPaginationConfig({
    ...initialTableState.pagination,
    currentTotalPages: currentData?.querySubmissions.pageInfo.totalPages,
    previousTotalPages: previousData?.querySubmissions.pageInfo.totalPages,
  });

  const tanstackColumnDefinitions: Array<
    ColumnDef<QuerySubmissionTableResult>
  > = [...generatedColumnDefinitions];

  const data =
    networkStatus === NetworkStatus.setVariables ? previousData : currentData;

  if (
    loadingColumnConfig ||
    !columnConfigResponse?.getSubmissionTableConfig.data ||
    loadingSavedViews ||
    !currentView
  ) {
    return null;
  }

  const loadingDetails = {
    loading,
    loadingText: `Loading ${tableContentName}`,
    noDataText: `No ${tableContentName} found`,
  };

  const requiredFields = SUBMISSIONS_TABLE_REQUIRED_FIELDS(tableName);
  if (tableName === TABLE_NAMES.PERMITS) {
    requiredFields.push({
      table: TABLE_NAMES.CERTIFICATE_UPLOADS,
      name: "status",
    });
  }

  const search = ({ page }: { page: number }) => {
    const variables: GetQuerySubmissionsQueryVariables = {
      description: {
        ...queryDescription,
        fields: mergeFieldGroups(queryDescription.fields, requiredFields),
      } satisfies QueryDescription,
      page,
    };

    void querySubmissions({
      variables,
    });
  };

  const localTableConfig = getLocalTableState();

  const { columnOrder: localColumnOrder } = localTableConfig;

  const updatedColumnOrder = sanityCheckLocalColumnOrder({
    localColumnOrder,
    defaultColumnIds: queryDescription.fields.map(field => fieldId(field)),
  });

  if (updatedColumnOrder) {
    setLocalColumnOrder(updatedColumnOrder);
  }

  const actionBarConfig =
    columnConfigResponse?.getSubmissionTableConfig.actionBarConfig;
  const enableSelectColumn = someActionsEnabled(actionBarConfig?.actions ?? {});

  const initialColumns = initializeColumns({
    initialTableState,
    tanstackColumnDefinitions,
    initialQueryDescription: queryDescription,
    actionsColumn,
    enableSelectColumn,
  });

  const timeoutError = error?.graphQLErrors.find(
    e => e.extensions.code === "TIMEOUT_ERROR",
  );

  const creatableSubmissionTypes =
    account?.submissionTypes.filter(
      submissionType =>
        submissionType.category === category &&
        hasPropertyAttachment(submissionType),
    ) ?? [];

  const isPermitting = category === SUBMISSION_CATEGORY.PERMITTING;

  if (creatableSubmissionTypes.length === 1) {
    tableActions.push(
      <CreateSubmissionButton
        key={"createSubmission"}
        disabled={false}
        onSubmit={submissionTypeId =>
          createSubmissionRedirect({
            history,
            submissionTypeId,
            isGlobal: !isPermitting,
          })
        }
        submissionTypeFilter={submissionType =>
          isValidSubmissionTypeForCategory(
            submissionType,
            category,
            isPermitting,
          )
        }
      />,
    );
  } else {
    const userCreatableSubmissionTypes = creatableSubmissionTypes.filter(
      submissionType =>
        canCreateSubmissionTypeFromAuthInfo({
          submissionType,
          user,
          admin,
          publicUser,
        }),
    );

    const actions = userCreatableSubmissionTypes.map(submissionType => ({
      label: submissionType.name,
      onClick: () => {
        createSubmissionRedirect({
          history,
          submissionTypeId: submissionType.id,
          isGlobal: !isPermitting,
        });
      },
      lineWrap: true,
      key: submissionType.id,
    }));

    tableActions.push(
      <DropdownMenu
        key={"createSubmission"}
        actions={actions}
        actionListWidth="360px"
        customButton={({ onPress }) => (
          <Button
            variant="primary"
            size="small"
            onPress={onPress}
            iconEnd="chevron-down"
            isDisabled={userCreatableSubmissionTypes.length === 0}
            title="Add new"
          />
        )}
        position="below-left"
      />,
    );
  }

  return (
    <FullWidthTable<QuerySubmissionTableResult>
      actions={tableActions}
      columns={initialColumns}
      previousData={previousData?.querySubmissions.data}
      currentData={data?.querySubmissions.data ?? []}
      loadingDetails={loadingDetails}
      manualPaginationConfig={{
        ...manualPaginationConfig,
        pageCount: data?.querySubmissions.pageInfo.totalPages ?? 1,
      }}
      initialState={initialTableState}
      filterable={{
        filterConfiguration: columnConfigResponse.getSubmissionTableConfig
          .data as Array<Attribute>,
        search,
      }}
      columnSettingProps={{
        columnConfiguration: columnConfigResponse.getSubmissionTableConfig.data,
        columnDefinitions: tanstackColumnDefinitions,
      }}
      timeoutError={timeoutError}
      queryDescription={queryDescription}
      updateQueryDescription={updateQueryDescription}
      savedViews={savedViewsResponse?.account?.savedViews}
      refetchSavedViews={refetchSavedViews}
      refetchTable={refetch}
      currentView={currentView}
      clientSideColumnConfig={
        columnConfigResponse.getSubmissionTableConfig.data
      }
      dataSourceComputationsAffectingQuery={
        data?.querySubmissions.dataSourceComputationsAffectingQuery
      }
      invalidQueryConfig={data?.querySubmissions.invalidQueryConfig ?? null}
      rowIdField={`${tableName}.id`}
      totalCount={data?.querySubmissions.pageInfo.totalCount ?? 0}
      actionBarConfig={actionBarConfig}
    />
  );
};

const SubmissionsTableWrapper = () => {
  const category = useParams<{ category: SUBMISSION_CATEGORY }>().category;
  const title = SUBMISSION_CATEGORY_TO_LABEL[category];

  return (
    <Layout title={`${title}`}>
      <SubmissionsTable category={category} key={category} />
    </Layout>
  );
};

export default SubmissionsTableWrapper;
